"use client";

import type { Transition } from "motion/react";
import { animate, motion } from "motion/react";
import {
  Children,
  isValidElement,
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type ArcDatum,
  buildArcs,
  buildHoverGrowTargets,
  buildSunburstEnterTiming,
  defaultSunburstGrowPadding,
  maxHoverSegmentThickness,
} from "./sunburst";
import {
  defaultSunburstColors,
  opacityForRelativeDepth,
  type SunburstContextValue,
  SunburstProvider,
} from "./sunburst-context";
import type { SunburstNode } from "./sunburst-data";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useViewerLanguage } from "@/lib/i18n";

export type { ArcDatum, Focus } from "./sunburst";
export type { SunburstNode } from "./sunburst-data";

const DEFAULT_HOVER_POP = 8;

/**
 * The one place an arc's color is decided. A legend beside the chart resolves
 * its markers through this too, so a row can never end up on a different
 * palette than the arc it points at.
 */
export function resolveSunburstArcColor(
  arc: Pick<ArcDatum, "categoryIndex" | "color">,
): string {
  return (
    arc.color ??
    (defaultSunburstColors[
      arc.categoryIndex % defaultSunburstColors.length
    ] as string)
  );
}

function componentDisplayName(child: ReactElement): string {
  return (
    (child.type as { displayName?: string }).displayName ||
    (child.type as { name?: string }).name ||
    ""
  );
}

function isDefsComponent(child: ReactElement): boolean {
  const name = componentDisplayName(child);
  return (
    name.includes("Gradient") ||
    name.includes("Pattern") ||
    name === "LinearGradient" ||
    name === "RadialGradient"
  );
}

function isOutsideSvgComponent(name: string): boolean {
  return name === "SunburstBreadcrumb" || name === "SunburstHint";
}

function isSunburstSegment(
  child: ReactNode
): child is ReactElement<{ index: number }> {
  return (
    isValidElement(child) && componentDisplayName(child) === "SunburstSegment"
  );
}

/** Inner rings last so parent segments win hit testing at ring boundaries. */
function sortSunburstSegments(
  segments: ReactElement<{ index: number }>[],
  arcs: ArcDatum[]
): ReactElement<{ index: number }>[] {
  return [...segments].sort((a, b) => {
    const arcA = arcs[a.props.index];
    const arcB = arcs[b.props.index];
    const depthA = arcA?.depth ?? 0;
    const depthB = arcB?.depth ?? 0;
    if (depthA !== depthB) {
      return depthB - depthA;
    }
    return (b.props.index ?? 0) - (a.props.index ?? 0);
  });
}

export interface SunburstChartProps {
  data: SunburstNode;
  size?: number;
  /** Bump to replay the initialization animation. */
  playKey?: number;
  className?: string;
  /** Controlled focus node id for drill-down. */
  focusId?: string;
  /** Called when focus changes via segment click or breadcrumb. */
  onFocusChange?: (focusId: string) => void;
  /** Controlled hover — arc index in the arcs array. */
  hoveredIndex?: number | null;
  onHoverChange?: (index: number | null) => void;
  /** Controlled selection — arc index in the arcs array. */
  selectedIndex?: number | null;
  onSelectChange?: (index: number | null) => void;
  hoverPop?: number;
  /** Inset reserved for hover growth; defaults from layout depth and hoverPop. */
  padding?: number;
  enterTransition?: Transition;
  enterStaggerScale?: number;
  children: ReactNode;
}

const SunburstChartCore = memo(function SunburstChartCore({
  data,
  size = 520,
  playKey = 0,
  className,
  focusId: focusIdProp,
  onFocusChange,
  hoveredIndex: hoveredIndexProp,
  onHoverChange,
  selectedIndex: selectedIndexProp,
  onSelectChange,
  hoverPop = DEFAULT_HOVER_POP,
  padding: paddingProp,
  enterTransition,
  enterStaggerScale = 1,
  children,
}: SunburstChartProps) {
  const fullRadius = size / 2;
  const hoverCapable = useHoverCapable();
  const { t } = useViewerLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const { arcs, maxDepth, focusById, rootId } = useMemo(
    () => buildArcs(data),
    [data]
  );

  const growPadding = useMemo(
    () => paddingProp ?? defaultSunburstGrowPadding(maxDepth, size, hoverPop),
    [paddingProp, maxDepth, size, hoverPop]
  );
  const radius = Math.max(8, fullRadius - growPadding);

  const [skipEnterAnimation, setSkipEnterAnimation] = useState(false);
  const [internalHoveredArc, setInternalHoveredArc] = useState<ArcDatum | null>(
    null
  );
  const [internalHoveredIndex, setInternalHoveredIndex] = useState<
    number | null
  >(null);
  const [internalSelectedIndex, setInternalSelectedIndex] = useState<
    number | null
  >(null);
  const growRef = useRef<Map<string, number>>(new Map());
  const [growTick, setGrowTick] = useState(0);

  const [internalFocusId, setInternalFocusId] = useState(rootId);
  const [prevFocusId, setPrevFocusId] = useState(rootId);
  const [zoomT, setZoomT] = useState(1);

  const isFocusControlled = focusIdProp !== undefined;
  const focusId = isFocusControlled ? focusIdProp : internalFocusId;

  const isHoverControlled = hoveredIndexProp !== undefined;
  const hoveredArcIndex = isHoverControlled
    ? hoveredIndexProp
    : internalHoveredIndex;
  const hoveredArc = useMemo(() => {
    if (hoveredArcIndex != null) {
      return arcs[hoveredArcIndex] ?? null;
    }
    return internalHoveredArc;
  }, [arcs, hoveredArcIndex, internalHoveredArc]);

  const isSelectionControlled = selectedIndexProp !== undefined;
  const selectedArcIndex = isSelectionControlled
    ? selectedIndexProp
    : internalSelectedIndex;
  const selectedArc = useMemo(
    () => (selectedArcIndex == null ? null : (arcs[selectedArcIndex] ?? null)),
    [arcs, selectedArcIndex]
  );

  const activeArcIndex = hoveredArcIndex ?? selectedArcIndex;
  const activeArc = hoveredArc ?? selectedArc;

  const setHoveredArcIndex = useCallback(
    (index: number | null) => {
      if (isHoverControlled) {
        onHoverChange?.(index);
      } else {
        setInternalHoveredIndex(index);
        setInternalHoveredArc(index == null ? null : (arcs[index] ?? null));
      }
    },
    [arcs, isHoverControlled, onHoverChange]
  );

  const setHoveredArc = useCallback(
    (arc: ArcDatum | null) => {
      setHoveredArcIndex(arc ? arc.arcIndex : null);
    },
    [setHoveredArcIndex]
  );

  const setSelectedArcIndex = useCallback(
    (index: number | null) => {
      if (isSelectionControlled) {
        onSelectChange?.(index);
      } else {
        setInternalSelectedIndex(index);
      }
    },
    [isSelectionControlled, onSelectChange]
  );

  const selectArcIndex = useCallback(
    (index: number) => {
      const next = selectedArcIndex === index ? null : index;
      setSelectedArcIndex(next);
      // A hover-controlled parent owns the highlight channel, so an older
      // legend selection would otherwise outrank the segment just tapped.
      setHoveredArcIndex(next);
      return next;
    },
    [selectedArcIndex, setSelectedArcIndex, setHoveredArcIndex]
  );

  const setFocusId = useCallback(
    (nextId: string) => {
      if (isFocusControlled) {
        onFocusChange?.(nextId);
      } else {
        setInternalFocusId(nextId);
      }
    },
    [isFocusControlled, onFocusChange]
  );

  const rootFocus = focusById.get(rootId);
  const focus = focusById.get(focusId) ?? rootFocus;
  const prevFocus = focusById.get(prevFocusId) ?? focus;

  useEffect(() => {
    if (!isFocusControlled) {
      setInternalFocusId(rootId);
    }
    setPrevFocusId(rootId);
    setZoomT(1);
  }, [rootId, isFocusControlled]);

  const knownArcsRef = useRef(arcs);
  const selectedArcIdRef = useRef<string | null>(null);

  /**
   * `selectedIndex` is a position in `arcs`, so new data silently re-points it at a
   * different node. The arc id is the durable identity: it is tracked while the data
   * holds still, then looked up in the rebuilt `arcs` — a selection that survived keeps
   * its node (at its new index), one that vanished is dropped, controlled or not.
   */
  useEffect(() => {
    if (knownArcsRef.current === arcs) {
      selectedArcIdRef.current =
        selectedArcIndex == null ? null : (arcs[selectedArcIndex]?.id ?? null);
      return;
    }
    knownArcsRef.current = arcs;

    const previousId = selectedArcIdRef.current;
    const nextIndex =
      previousId == null
        ? -1
        : arcs.findIndex((arc) => arc.id === previousId);

    if (nextIndex === -1) {
      selectedArcIdRef.current = null;
      if (selectedArcIndex != null) {
        setSelectedArcIndex(null);
      }
      return;
    }
    if (nextIndex !== selectedArcIndex) {
      setSelectedArcIndex(nextIndex);
    }
  }, [arcs, selectedArcIndex, setSelectedArcIndex]);

  const enterTiming = useMemo(
    () => buildSunburstEnterTiming(arcs, enterStaggerScale),
    [arcs, enterStaggerScale]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: replay when data or playKey changes
  useEffect(() => {
    setSkipEnterAnimation(
      typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
    );
  }, [playKey, arcs]);

  const prefersReduced = () =>
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const growControls = useRef<ReturnType<typeof animate> | null>(null);
  const zoomControls = useRef<ReturnType<typeof animate> | null>(null);
  const zoomGen = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: prefersReduced reads matchMedia at call time
  const zoomTo = useCallback(
    (nextId: string) => {
      if (nextId === focusId) {
        return;
      }
      setHoveredArc(null);
      setSelectedArcIndex(null);
      growControls.current?.stop();
      growRef.current = new Map();
      setGrowTick((n) => n + 1);
      setPrevFocusId(focusId);
      setFocusId(nextId);
      zoomControls.current?.stop();
      const gen = ++zoomGen.current;
      if (prefersReduced()) {
        setZoomT(1);
        return;
      }
      setZoomT(0);
      zoomControls.current = animate(0, 1, {
        duration: 0.75,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (value) => {
          if (zoomGen.current === gen) {
            setZoomT(value);
          }
        },
        onComplete: () => {
          if (zoomGen.current === gen) {
            setZoomT(1);
            setPrevFocusId(nextId);
          }
        },
      });
    },
    [focusId, setFocusId, setHoveredArc, setSelectedArcIndex]
  );

  const isDescendant = useCallback(
    (d: ArcDatum, ancestorId: string) =>
      d.id === ancestorId || d.id.startsWith(`${ancestorId} / `),
    []
  );

  const isOnHoverPath = useCallback(
    (d: ArcDatum, hoveredId: string) =>
      d.id === hoveredId || hoveredId.startsWith(`${d.id} / `),
    []
  );

  const isRelated = useCallback(
    (d: ArcDatum) => {
      if (!activeArc) {
        return true;
      }
      return (
        isDescendant(d, activeArc.id) || activeArc.id.startsWith(`${d.id} / `)
      );
    },
    [activeArc, isDescendant]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: grow targets from visible hover path
  useEffect(() => {
    const targets =
      activeArc && focus
        ? buildHoverGrowTargets(
            arcs,
            activeArc,
            focus,
            maxDepth,
            radius,
            hoverPop,
            isOnHoverPath
          )
        : new Map<string, number>();
    const starts = new Map(growRef.current);
    const ids = new Set<string>([...starts.keys(), ...targets.keys()]);

    growControls.current?.stop();

    if (prefersReduced()) {
      growRef.current = targets;
      setGrowTick((n) => n + 1);
      return;
    }

    growControls.current = animate(0, 1, {
      duration: 0.42,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (p) => {
        const next = new Map<string, number>();
        for (const id of ids) {
          const start = starts.get(id) ?? 0;
          const target = targets.get(id) ?? 0;
          const val = start + (target - start) * p;
          if (val > 0.01) {
            next.set(id, val);
          }
        }
        growRef.current = next;
        setGrowTick((n) => n + 1);
      },
    });
    return () => growControls.current?.stop();
  }, [activeArc, arcs, hoverPop, isOnHoverPath, focus, maxDepth, radius]);

  const getColor = useCallback(
    (categoryIndex: number, nodeColor?: string) =>
      resolveSunburstArcColor({ categoryIndex, color: nodeColor }),
    [],
  );

  const getFill = useCallback(
    (arcIndex: number, fillOverride?: string, colorOverride?: string) => {
      if (fillOverride) {
        return fillOverride;
      }
      const arc = arcs[arcIndex];
      if (!arc) {
        return defaultSunburstColors[0] as string;
      }
      return (
        colorOverride ?? arc.fill ?? arc.color ?? getColor(arc.categoryIndex)
      );
    },
    [arcs, getColor]
  );

  const getFillOpacity = useCallback(
    (relativeDepth: number, override?: number) =>
      override ?? opacityForRelativeDepth(relativeDepth),
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: growTick forces re-read from growRef
  const growAmountForArc = useCallback(
    (arcId: string) => growRef.current.get(arcId) ?? 0,
    [growTick]
  );

  const maxExpandedThickness = useMemo(
    () => maxHoverSegmentThickness(maxDepth, radius, hoverPop),
    [maxDepth, radius, hoverPop]
  );

  if (!(focus && prevFocus)) {
    return null;
  }

  const childArray = Children.toArray(children);
  const defsChildren = childArray.filter(
    (child): child is ReactElement =>
      isValidElement(child) && isDefsComponent(child)
  );

  const outsideChildren: ReactNode[] = [];
  const svgChildren: ReactNode[] = [];

  for (const child of childArray) {
    if (!isValidElement(child)) {
      svgChildren.push(child);
      continue;
    }
    if (isDefsComponent(child)) {
      continue;
    }
    const name = componentDisplayName(child);
    if (isOutsideSvgComponent(name)) {
      outsideChildren.push(child);
    } else {
      svgChildren.push(child);
    }
  }

  const segmentChildren = svgChildren.filter(isSunburstSegment);
  const otherSvgChildren = svgChildren.filter(
    (child) => !isSunburstSegment(child)
  );
  const orderedSegments = sortSunburstSegments(segmentChildren, arcs);
  const orderedSvgChildren = [...orderedSegments, ...otherSvgChildren];

  const providerValue: SunburstContextValue = {
    data,
    arcs,
    focusById,
    rootId,
    maxDepth,
    radius,
    size,
    focus,
    prevFocus,
    focusId,
    zoomTo,
    zoomT,
    enterTiming,
    skipEnterAnimation,
    growAmountForArc,
    getColor,
    getFill,
    getFillOpacity,
    isRelated,
    isDescendant,
    enterTransition,
    enterStaggerScale,
    playKey,
    hoverPop,
    maxExpandedThickness,
    containerRef,
    hoveredArcIndex: activeArcIndex,
    setHoveredArcIndex,
    hoveredArc: activeArc,
    setHoveredArc,
    selectedArcIndex,
    selectArcIndex,
  };

  return (
    <SunburstProvider value={providerValue}>
      <div
        className={className}
        ref={containerRef}
        style={{ maxWidth: "100%", width: size }}
      >
        {outsideChildren.filter(
          (child) =>
            isValidElement(child) &&
            componentDisplayName(child) === "SunburstBreadcrumb"
        )}
        <div
          className="mx-auto w-full"
          style={{ aspectRatio: "1 / 1", maxWidth: size }}
        >
          <motion.svg
            animate={{ opacity: 1 }}
            aria-label={t("Sunburst chart of {name}", { name: data.name })}
            initial={{ opacity: 0 }}
            onPointerLeave={
              hoverCapable ? () => setHoveredArc(null) : undefined
            }
            role="img"
            style={{ display: "block", overflow: "visible" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            viewBox={`${-fullRadius} ${-fullRadius} ${size} ${size}`}
            width="100%"
          >
            {defsChildren.length > 0 ? <defs>{defsChildren}</defs> : null}
            {orderedSvgChildren}
          </motion.svg>
        </div>
        {outsideChildren.filter(
          (child) =>
            isValidElement(child) &&
            componentDisplayName(child) === "SunburstHint"
        )}
      </div>
    </SunburstProvider>
  );
});

export function SunburstChart(props: SunburstChartProps) {
  return <SunburstChartCore {...props} />;
}

SunburstChart.displayName = "SunburstChart";
